"use client";

import { faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState, type ReactElement } from "react";
import ReactMarkdown from "react-markdown";
import { twMerge } from "tailwind-merge";

import { getLocalizedField, type Language } from "@rootreeweb/multi-language";
import type { HTMLAttributes } from "@rootreeweb/ui";

import type { FAQProps, FAQQuestion } from "../types.js";

type Props = {
	question: FAQQuestion;
	faqProps: FAQProps;
	lang: Language;
};

/**
 *
 *
 * @param props The props for the component.
 * @returns The component.
 */
export default function Question(props: Props): ReactElement {
	const { question, faqProps, lang } = props;

	const [open, setOpen] = useState(faqProps.show);
	const Tag = faqProps.hide_accordion ? "div" : "button";
	const tagProps: HTMLAttributes<HTMLButtonElement> &
		HTMLAttributes<HTMLHeadingElement> = {
		className:
			"faq:justify-space-between faq:flex faq:w-full faq:text-2xl faq:grow faq:items-center faq:rounded-b-none faq:rounded-t-xl faq:border-0 faq:bg-white faq:text-left faq:font-bold",
	};
	if (Tag === "button") {
		tagProps.onClick = () => setOpen(!open);
		tagProps.className = twMerge(
			tagProps.className,
			"faq:cursor-pointer faq:p-4 faq:text-lg faq:text-deepocean-500 faq:transition-all faq:duration-[0.125s] faq:ease-in-out faq:hover:bg-cyan-500/10 faq:hover:text-deepocean-800",
		);
	}

	const questionText = getLocalizedField<string>(question, "question_", lang);
	const answerText = getLocalizedField<string>(question, "answer_", lang);
	return (
		<dl
			className="faq-question faq:mb-4 faq:overflow-hidden faq:rounded-xl faq:border faq:border-solid faq:border-site-500/25 faq:bg-white faq:shadow"
			data-testid="faq-entry"
		>
			<dt className="faq:flex faq:text-[1.125rem]" data-testid="faq-question">
				<Tag {...tagProps}>
					<span className="faq:grow faq:text-left faq:font-bold">
						{questionText}
					</span>
					{!faqProps.hide_accordion &&
						(open ? (
							<FontAwesomeIcon icon={faMinus} />
						) : (
							<FontAwesomeIcon icon={faPlus} />
						))}
				</Tag>
			</dt>
			{open && (
				<dd className="faq:mx-6 faq:max-w-[80ch]" data-testid="faq-answer">
					<ReactMarkdown>{answerText}</ReactMarkdown>
				</dd>
			)}
		</dl>
	);
}
